import React, { useEffect, useState } from 'react';
import { graphBuilderApi } from '../../services/api';
import './Explainability.css';

/**
 * StabilityCard
 *
 * Calls POST /api/explain/stability and renders a robustness indicator:
 * how often the same decision is reached when individual evidence
 * factors are perturbed. Shows nothing when no query_run_id is
 * available (older evaluations from the non-streaming path).
 *
 * Props:
 *  - queryRunId:   string  — required to call the stability endpoint
 *  - domain:       string  — vocabulary domain (general/aml/healthcare/insurance)
 */
const StabilityCard = ({ queryRunId, domain = 'general' }) => {
  const [state, setState] = useState({ phase: 'idle', data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    if (!queryRunId) {
      setState({ phase: 'unavailable', data: null, error: null });
      return;
    }

    setState({ phase: 'loading', data: null, error: null });
    graphBuilderApi
      .getDecisionStability({ queryRunId, domain })
      .then((data) => {
        if (cancelled) return;
        setState({ phase: 'ready', data, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ phase: 'error', data: null, error: err?.message || 'Failed to load stability.' });
      });

    return () => { cancelled = true; };
  }, [queryRunId, domain]);

  if (state.phase === 'unavailable') {
    return (
      <div className="explain-stability-card explain-stability-card--muted">
        <div className="explain-stability-header">
          <span className="explain-stability-title">Robustness</span>
        </div>
        <p className="explain-stability-note">
          Robustness analysis is only available for evaluations run via
          the streaming Test Graph path. Older evaluations may not have
          a stability score.
        </p>
      </div>
    );
  }

  if (state.phase === 'loading') {
    return (
      <div className="explain-stability-card">
        <div className="explain-stability-header">
          <span className="explain-stability-title">Robustness</span>
        </div>
        <p className="explain-stability-note">Analyzing decision stability…</p>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div className="explain-stability-card explain-stability-card--error">
        <div className="explain-stability-header">
          <span className="explain-stability-title">Robustness</span>
        </div>
        <p className="explain-stability-note">{state.error}</p>
      </div>
    );
  }

  // ready
  const data = state.data || {};
  // The /stability endpoint shape varies; tolerate either {stability_score}
  // or {robustness: {score}} or {summary, details}.
  const rawScore =
    data.stability_score ??
    data.robustness?.score ??
    data.score ??
    null;
  const pct = rawScore == null
    ? null
    : Math.max(0, Math.min(100, Math.round((rawScore <= 1 ? rawScore * 100 : rawScore))));
  const summary =
    data.summary ||
    data.robustness?.summary ||
    data.message ||
    '';

  let band = 'medium';
  if (pct != null) {
    if (pct >= 80) band = 'high';
    else if (pct < 50) band = 'low';
  }

  return (
    <div className={`explain-stability-card explain-stability-card--${band}`}>
      <div className="explain-stability-header">
        <span className="explain-stability-title">Robustness</span>
        {pct != null && (
          <span className="explain-stability-pct">{pct}%</span>
        )}
      </div>
      {pct != null && (
        <div className="explain-stability-bar">
          <div className="explain-stability-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      )}
      <p className="explain-stability-note">
        {summary || (
          pct == null
            ? 'No stability score returned.'
            : pct >= 80
              ? `High stability — about ${pct}% of perturbations preserve this decision.`
              : pct < 50
                ? `Low stability — only about ${pct}% of perturbations preserve this decision; the outcome could shift with minor evidence changes.`
                : `Moderate stability — about ${pct}% of perturbations preserve this decision.`
        )}
      </p>
    </div>
  );
};

export default StabilityCard;
